import { useEffect, useRef } from "react";
import { getModelContext, registerTools } from "./modelContext";

/**
 * Déclare des outils aux assistants IA le temps que le composant est monté.
 *
 * Sans WebMCP dans le navigateur — le cas de presque tous les visiteurs —
 * rien d'autre ne se passe : `load` n'est jamais appelé, et le code des outils
 * ne se télécharge pas. Sinon, `load` importe le module qui les construit, et
 * les outils sont retirés quand le composant disparaît ou que `key` change.
 *
 * Les fonctions passées en dépendance sont relayées par une référence tenue à
 * jour à chaque rendu : un outil appelé lit toujours la dernière partie et la
 * dernière fonction de navigation, sans qu'on réenregistre tout à chaque
 * changement de score. Les valeurs simples (l'origine du site) sont figées à
 * l'enregistrement.
 *
 * @param {() => Promise<(deps: object) => object[]>} load rend la fabrique d'outils
 * @param {object} deps
 * @param {string} [key] réenregistre quand il change (l'identifiant de partie)
 */
export function useAgentTools(load, deps, key) {
    const depsRef = useRef(deps);
    depsRef.current = deps;
    const loadRef = useRef(load);

    useEffect(() => {
        const context = getModelContext();
        if (!context) return undefined;
        let cancelled = false;
        let unregister = null;
        loadRef.current().then(
            (build) => {
                if (cancelled) return;
                const live = {};
                for (const [name, value] of Object.entries(depsRef.current)) {
                    live[name] =
                        typeof value === "function" ? (...args) => depsRef.current[name](...args) : value;
                }
                unregister = registerTools(context, build(live));
            },
            (error) => console.warn("[agent] outils non chargés :", error),
        );
        return () => {
            cancelled = true;
            unregister?.();
        };
    }, [key]);
}
